import React, { useState, useEffect } from "react"
import LocalizationItemDialog from "aldoo-ra/Localization/localization-item-dialog"
import LocalizationController from "aldoo-ra/Localization/localization-controller"
import { showNotification } from "aldoo-ra/Notification"
import NewsletterAPI from "../../api"

const TemplateVariablesModal = ({ 
  isOpen, 
  onClose, 
  templateId, 
  templateName, 
  releaseId,
  onSave 
}) => {
  const [localizationItem, setLocalizationItem] = useState(null)
  const [loading, setLoading] = useState(false)
  const [languagesLoaded, setLanguagesLoaded] = useState(false)
  const controller = LocalizationController()

  // Load languages when modal opens
  useEffect(() => {
    if (isOpen && !languagesLoaded) {
      loadLanguages()
    }
  }, [isOpen])

  // Load or create localization item when modal opens and languages are loaded
  useEffect(() => {
    if (isOpen && templateId && releaseId && languagesLoaded) {
      loadOrCreateLocalizationItem()
    }
  }, [isOpen, templateId, releaseId, languagesLoaded])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLocalizationItem(null)
      setLanguagesLoaded(false)
    }
  }, [isOpen])

  const loadLanguages = async () => {
    try {
      setLoading(true)
      
      // Get language statistics to populate the controller
      const languageStats = await controller.getLanguageStatistics()
      
      if (languageStats && languageStats.languages) {
        // Set languages in the controller
        controller.setLanguages(languageStats.languages)
        
        // Find and set primary language
        const primaryLanguage = languageStats.languages.find(lang => lang.primary)
        if (primaryLanguage) {
          controller.setPrimaryLanguage(primaryLanguage.code)
        }
        
        setLanguagesLoaded(true)
      }
    } catch (error) {
      console.error('Failed to load languages:', error)
      showNotification({
        message: "Failed to load languages",
        className: "bg-admin_error text-white"
      })
    } finally {
      setLoading(false)
    }
  }

  const loadOrCreateLocalizationItem = async () => {
    try {
      setLoading(true)
      
      let localizationKey = `newsletter_release_${releaseId}_template_${templateId}_variables`
      
      // Try to get existing localization item
      let existingItems = await controller.getLocalizationKey(localizationKey)
      
      if (existingItems && existingItems.length > 0) {
        // Use existing localization item
        setLocalizationItem(existingItems[0])
      } else {
        // Get the original template variables to use as defaults
        const templateResponse = await NewsletterAPI.getTemplateVariables(templateId)
        const defaultVariables = templateResponse?.variables || {}
        
        // Convert object variables to array format for the object editor
        const variablesArray = Object.entries(defaultVariables).map(([key, value]) => ({
          key,
          value: String(value)
        }))
        
        // Create the localization entry with original template variables as defaults
        // Backend will automatically populate all languages for object types
        await controller.addLocalizationKey({
          key: localizationKey,
          type: 'object',
          language: controller.primaryLanguage || 'en',
          content: variablesArray
        })
        
        // Get the newly created item
        const newItems = await controller.getLocalizationKey(localizationKey)
        if (newItems && newItems.length > 0) {
          setLocalizationItem(newItems[0])
        }
      }
    } catch (error) {
      console.error('Failed to load template variables:', error)
      showNotification({
        message: "Failed to load template variables",
        className: "bg-admin_error text-white"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (updatedItem) => {
    try {
      // Update the release template to reference this localization entry
      const localizationId = updatedItem._id
      
      // Save the localization reference to the release
      await NewsletterAPI.saveReleaseTemplateVariables(releaseId, templateId, localizationId)
      
      showNotification({
        message: "Template variables saved successfully!",
        className: "bg-admin_success text-white"
      })
      
      if (onSave) onSave()
      onClose()
    } catch (error) {
      console.error('Failed to save template variables:', error)
      showNotification({
        message: "Failed to save template variables",
        className: "bg-admin_error text-white"
      })
    }
  }

  // Don't render the dialog if we don't have the localization item ready
  if (!isOpen || loading || !localizationItem || !languagesLoaded) {
    return null
  }

  return (
    <LocalizationItemDialog
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      item={localizationItem}
      controller={controller}
      parentObject={null}
    />
  )
}

export default TemplateVariablesModal